import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Activity, Calendar, Users, Package,
} from 'lucide-react'

import { api } from '@/lib/api/client'
import type {
  ExpansionContractionReportResponse, MrrReportResponse,
  SubscriptionChangesReportResponse,
} from '@/types/api'


// ── Utils ────────────────────────────────────────────────────────────────────


function fmtMoney(n: number, currency = 'SEK'): string {
  return new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(n) + ' ' + currency
}

function fmtNumber(n: number): string {
  return new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(n)
}

function fmtSigned(n: number, currency = 'SEK'): string {
  const prefix = n > 0 ? '+' : n < 0 ? '−' : ''
  return prefix + fmtMoney(Math.abs(n), currency)
}

function thisMonthRange(): { start: string; end: string } {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return { start: fmt(start), end: fmt(end) }
}


// ── Page ─────────────────────────────────────────────────────────────────────


type Tab = 'mrr' | 'changes' | 'delta'

const TABS: { key: Tab; label: string }[] = [
  { key: 'mrr',     label: 'MRR snapshot'   },
  { key: 'delta',   label: 'MRR delta'      },
  { key: 'changes', label: 'Activity'       },
]


export default function SubscriptionReportsPage() {
  const [tab, setTab] = useState<Tab>('mrr')
  const range = thisMonthRange()
  const [asOf,        setAsOf]        = useState(range.end)
  const [periodStart, setPeriodStart] = useState(range.start)
  const [periodEnd,   setPeriodEnd]   = useState(range.end)

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Subscriptions · Reports</div>
          <h1 className="page-title">Billing reports</h1>
          <div className="page-subtitle">
            Point-in-time MRR with by-customer / by-product breakdowns, plus event-driven
            change and expansion/contraction summaries. Reads the audit log from PR 3 — no
            historical replay needed.
          </div>
        </div>
      </div>

      <div className="tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'mrr' && (
        <MrrTab asOf={asOf} onAsOfChange={setAsOf} />
      )}
      {tab === 'delta' && (
        <DeltaTab
          periodStart={periodStart} periodEnd={periodEnd}
          onPeriodStart={setPeriodStart} onPeriodEnd={setPeriodEnd}
        />
      )}
      {tab === 'changes' && (
        <ChangesTab
          periodStart={periodStart} periodEnd={periodEnd}
          onPeriodStart={setPeriodStart} onPeriodEnd={setPeriodEnd}
        />
      )}
    </div>
  )
}


// ── MRR snapshot tab ────────────────────────────────────────────────────────


function MrrTab({ asOf, onAsOfChange }: { asOf: string; onAsOfChange: (s: string) => void }) {
  const q = useQuery<MrrReportResponse>({
    queryKey: ['report-mrr', asOf],
    queryFn:  () => api.get(`/reports/mrr?asOf=${asOf}`),
  })

  const totalLineCount = useMemo(() => {
    return (q.data?.byCustomer ?? []).reduce((s, c) => s + c.lineCount, 0)
  }, [q.data])

  return (
    <>
      <div className="card" style={{ padding: 14, marginBottom: 12, display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <FieldLabel label="As of">
          <input type="date" className="input" value={asOf} onChange={e => onAsOfChange(e.target.value)} />
        </FieldLabel>
        <div className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
          MRR = sum of monthly unit_price across every ACTIVE subscription line. PAUSED + PENDING + CANCELLED excluded.
        </div>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 12 }}>
        <StatCard icon={Activity} label="MRR"           value={q.data ? fmtMoney(q.data.total) : '—'} accent="info" />
        <StatCard icon={Users}    label="Customers"     value={String(q.data?.byCustomer.length ?? 0)} accent="info" />
        <StatCard icon={Package}  label="Active lines"  value={String(totalLineCount)}                 accent="info" />
      </div>

      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ns-border)', fontWeight: 700, fontSize: 13 }}>
            By customer
          </div>
          <BreakdownTable
            rows={(q.data?.byCustomer ?? []).map(c => ({
              label: c.name,
              sub:   `${c.lineCount} line${c.lineCount === 1 ? '' : 's'}`,
              value: c.mrr,
              total: q.data?.total ?? 0,
            }))}
            isLoading={q.isLoading}
            currency="SEK"
          />
        </div>
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ns-border)', fontWeight: 700, fontSize: 13 }}>
            By product type
          </div>
          <BreakdownTable
            rows={(q.data?.byProductType ?? []).map(p => ({
              label: p.productType,
              sub:   `${p.lineCount} line${p.lineCount === 1 ? '' : 's'}`,
              value: p.mrr,
              total: q.data?.total ?? 0,
            }))}
            isLoading={q.isLoading}
            currency="SEK"
          />
        </div>
      </div>
    </>
  )
}


// ── MRR delta tab (expansion / contraction) ────────────────────────────────


function DeltaTab({
  periodStart, periodEnd, onPeriodStart, onPeriodEnd,
}: { periodStart: string; periodEnd: string; onPeriodStart: (s: string) => void; onPeriodEnd: (s: string) => void }) {
  const q = useQuery<ExpansionContractionReportResponse>({
    queryKey: ['report-delta', periodStart, periodEnd],
    queryFn:  () => api.get(`/reports/expansion-contraction?periodStart=${periodStart}&periodEnd=${periodEnd}`),
  })

  return (
    <>
      <PeriodPicker
        periodStart={periodStart} periodEnd={periodEnd}
        onPeriodStart={onPeriodStart} onPeriodEnd={onPeriodEnd}
        hint="MRR moves are computed from the audit log: LINE_ADDED → expansion, LINE_ENDED → contraction (price looked up from the line's last period), price changes split by sign."
      />

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))', marginBottom: 12 }}>
        <StatCard icon={TrendingUp}   label="Expansion"    value={q.data ? `+${fmtMoney(q.data.expansion)}` : '—'}     accent="ok"  />
        <StatCard icon={TrendingDown} label="Contraction"  value={q.data ? `−${fmtMoney(q.data.contraction)}` : '—'}   accent="warn"/>
        <StatCard
          icon={q.data && q.data.netDelta >= 0 ? ArrowUpRight : ArrowDownRight}
          label="Net MRR delta"
          value={q.data ? fmtSigned(q.data.netDelta) : '—'}
          accent={q.data && q.data.netDelta >= 0 ? 'ok' : 'warn'}
        />
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ns-border)', fontWeight: 700, fontSize: 13 }}>
          By event type
        </div>
        {q.isLoading && <div className="muted" style={{ padding: 18, textAlign: 'center' }}>Loading…</div>}
        {!q.isLoading && (q.data?.byEventType ?? []).length === 0 && (
          <div className="muted" style={{ padding: 18, textAlign: 'center' }}>No events in this period.</div>
        )}
        {!q.isLoading && (q.data?.byEventType ?? []).length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Event</th>
                <th style={{ textAlign: 'right' }}>Count</th>
                <th style={{ textAlign: 'right' }}>+ Expansion</th>
                <th style={{ textAlign: 'right' }}>− Contraction</th>
                <th style={{ textAlign: 'right' }}>Net</th>
              </tr>
            </thead>
            <tbody>
              {q.data!.byEventType
                  .filter(r => r.count > 0)
                  .map(r => {
                const net = r.expansion - r.contraction
                return (
                  <tr key={r.eventType}>
                    <td><code style={{ fontSize: 12 }}>{r.eventType}</code></td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{fmtNumber(r.count)}</td>
                    <td className="tabular" style={{ textAlign: 'right', color: r.expansion > 0 ? 'var(--ns-ok)' : 'var(--ns-text-muted)' }}>
                      {r.expansion > 0 ? `+${fmtMoney(r.expansion)}` : '—'}
                    </td>
                    <td className="tabular" style={{ textAlign: 'right', color: r.contraction > 0 ? 'var(--ns-warn)' : 'var(--ns-text-muted)' }}>
                      {r.contraction > 0 ? `−${fmtMoney(r.contraction)}` : '—'}
                    </td>
                    <td className="tabular" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {net !== 0 ? fmtSigned(net) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}


// ── Activity / event-count tab ──────────────────────────────────────────────


function ChangesTab({
  periodStart, periodEnd, onPeriodStart, onPeriodEnd,
}: { periodStart: string; periodEnd: string; onPeriodStart: (s: string) => void; onPeriodEnd: (s: string) => void }) {
  const q = useQuery<SubscriptionChangesReportResponse>({
    queryKey: ['report-changes', periodStart, periodEnd],
    queryFn:  () => api.get(`/reports/subscription-changes?periodStart=${periodStart}&periodEnd=${periodEnd}`),
  })

  const maxCount = useMemo(() => {
    return Math.max(1, ...(q.data?.byEventType ?? []).map(r => r.count))
  }, [q.data])

  return (
    <>
      <PeriodPicker
        periodStart={periodStart} periodEnd={periodEnd}
        onPeriodStart={onPeriodStart} onPeriodEnd={onPeriodEnd}
        hint="Counts every audit event in core.subscription_events for the period."
      />

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', marginBottom: 12 }}>
        <StatCard icon={Activity} label="Total events" value={q.data ? fmtNumber(q.data.total) : '—'} accent="info" />
        <StatCard icon={Calendar} label="Distinct types" value={String(q.data?.byEventType.length ?? 0)} accent="info" />
      </div>

      <div className="card" style={{ padding: 0 }}>
        {q.isLoading && <div className="muted" style={{ padding: 18, textAlign: 'center' }}>Loading…</div>}
        {!q.isLoading && (q.data?.byEventType ?? []).length === 0 && (
          <div className="muted" style={{ padding: 18, textAlign: 'center' }}>No events in this period.</div>
        )}
        {!q.isLoading && (q.data?.byEventType ?? []).length > 0 && (
          <table className="table">
            <thead>
              <tr><th>Event type</th><th style={{ textAlign: 'right' }}>Count</th><th>Share</th></tr>
            </thead>
            <tbody>
              {q.data!.byEventType.map(r => (
                <tr key={r.eventType}>
                  <td><code style={{ fontSize: 12 }}>{r.eventType}</code></td>
                  <td className="tabular" style={{ textAlign: 'right' }}>{fmtNumber(r.count)}</td>
                  <td style={{ width: '40%' }}>
                    <div style={{
                      height: 8, borderRadius: 4,
                      background: 'var(--ns-surface-2)',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${(r.count / maxCount) * 100}%`,
                        height: '100%',
                        background: 'var(--ns-brand-strong)',
                      }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}


// ── Sub-components ──────────────────────────────────────────────────────────


function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  )
}


function PeriodPicker({
  periodStart, periodEnd, onPeriodStart, onPeriodEnd, hint,
}: { periodStart: string; periodEnd: string; onPeriodStart: (s: string) => void; onPeriodEnd: (s: string) => void; hint?: string }) {
  return (
    <div className="card" style={{ padding: 14, marginBottom: 12, display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <FieldLabel label="Period start">
        <input type="date" className="input" value={periodStart} onChange={e => onPeriodStart(e.target.value)} />
      </FieldLabel>
      <FieldLabel label="Period end">
        <input type="date" className="input" value={periodEnd} onChange={e => onPeriodEnd(e.target.value)} />
      </FieldLabel>
      {hint && <div className="muted" style={{ fontSize: 12, alignSelf: 'center', flex: 1 }}>{hint}</div>}
    </div>
  )
}


function StatCard({
  icon: Icon, label, value, accent,
}: { icon: typeof Activity; label: string; value: string; accent: 'ok' | 'warn' | 'err' | 'info' }) {
  const color = {
    ok:   'var(--ns-ok)',
    warn: 'var(--ns-warn)',
    err:  'var(--ns-err)',
    info: 'var(--ns-brand-strong)',
  }[accent]
  return (
    <div className="stat" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon size={13} style={{ color }} /> {label}
      </div>
      <div className="stat-value tabular">{value}</div>
    </div>
  )
}


function BreakdownTable({
  rows, isLoading, currency,
}: {
  rows: { label: string; sub: string; value: number; total: number }[]
  isLoading: boolean
  currency: string
}) {
  if (isLoading) return <div className="muted" style={{ padding: 18, textAlign: 'center' }}>Loading…</div>
  if (rows.length === 0) return <div className="muted" style={{ padding: 18, textAlign: 'center' }}>No data.</div>
  return (
    <table className="table">
      <thead>
        <tr><th></th><th style={{ textAlign: 'right' }}>MRR</th><th>Share</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const pct = r.total > 0 ? (r.value / r.total) * 100 : 0
          return (
            <tr key={i}>
              <td>
                <div style={{ fontWeight: 600 }}>{r.label}</div>
                <div className="muted" style={{ fontSize: 11 }}>{r.sub}</div>
              </td>
              <td className="tabular" style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {fmtMoney(r.value, currency)}
              </td>
              <td style={{ width: '40%' }}>
                <div style={{
                  height: 8, borderRadius: 4, background: 'var(--ns-surface-2)', overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${pct}%`, height: '100%',
                    background: 'var(--ns-brand-strong)',
                  }} />
                </div>
                <div className="muted tabular" style={{ fontSize: 11, marginTop: 2, textAlign: 'right' }}>
                  {pct.toFixed(1)}%
                </div>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
